import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";

export const fetchUnprocessedOrders = createAsyncThunk(
  "orders/fetchUnprocessedOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("orders/dispatcher/");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders");
    }
  }
);

export const fetchDispatcherOrders = createAsyncThunk(
  "orders/fetchDispatcherOrders",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const user = state.auth.user;

      if (!user?.id) {
        throw new Error("User not found in store");
      }

      const response = await axiosInstance.get(`orders/dispatcher/${user.id}`);
      const data = response.data;
      const sortedData = data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      return sortedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders");
    }
  }
);

export const updateOrder = createAsyncThunk(
  "orders/updateOrder",
  async ({ orderId, updates }, { rejectWithValue }) => {
    try {
      console.log("updates:");
      console.log(updates);
      const response = await axiosInstance.put(`orders/${orderId}`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update order");
    }
  }
);

export const changeOrderStatus = createAsyncThunk(
  "orders/changeOrderStatus",
  async ({ orderId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`orders/status/`, {
        ...updates,
        id: orderId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to change order status"
      );
    }
  }
);

export const addOrderNote = createAsyncThunk(
  "orders/addOrderNote",
  async ({ orderId, note, from }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put("orders/note/", {
        id: orderId,
        note,
        from,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add note");
    }
  }
);

const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    unprocessed: {
      data: [],
      loading: null,
      error: null,
    },
    dispatcher: {
      data: [],
      loading: null,
      error: null,
    },
  },
  reducers: {
    cleanReducer(state) {
      state.unprocessed.data = [];
      state.dispatcher.data = [];
    },

    addUnprocessedOrder(state, action) {
      const exists = state.unprocessed.data.some(
        (o) => o.id === action.payload.id
      );
      if (!exists) {
        state.unprocessed.data.unshift(action.payload);
      }
    },

    // update order if it exists
    mergeOrderUpdate(state, action) {
      const updated = action.payload;

      // Update in unprocessed list
      const unIndex = state.unprocessed.data.findIndex(
        (o) => o.id === updated.id
      );
      if (unIndex !== -1) {
        state.unprocessed.data[unIndex] = updated;
      }

      // Update in dispatcher list
      const disIndex = state.dispatcher.data.findIndex(
        (o) => o.id === updated.id
      );
      if (disIndex !== -1) {
        state.dispatcher.data[disIndex] = updated;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // UNPROCESSED ORDERS
      .addCase(fetchUnprocessedOrders.pending, (state) => {
        state.unprocessed.loading = true;
        state.unprocessed.error = null;
      })
      .addCase(fetchUnprocessedOrders.fulfilled, (state, action) => {
        state.unprocessed.loading = false;
        // state.unprocessed.data = action.payload;
        // Merge incoming orders with current list
        const incoming = action.payload;

        incoming.forEach((order) => {
          const index = state.unprocessed.data.findIndex(
            (o) => o.id === order.id
          );

          if (index !== -1) {
            // Update existing order
            state.unprocessed.data[index] = order;
          } else {
            // Add new order
            state.unprocessed.data.unshift(order);
          }
        });
      })
      .addCase(fetchUnprocessedOrders.rejected, (state, action) => {
        state.unprocessed.loading = false;
        state.unprocessed.error =
          action.payload || "Error loading unprocessed orders";
      })

      // DISPATCHER ORDERS
      .addCase(fetchDispatcherOrders.pending, (state) => {
        state.dispatcher.loading = true;
        state.dispatcher.error = null;
      })
      .addCase(fetchDispatcherOrders.fulfilled, (state, action) => {
        state.dispatcher.loading = false;
        // state.dispatcher.data = action.payload;

        const incoming = action.payload;

        incoming.forEach((order) => {
          const index = state.dispatcher.data.findIndex(
            (o) => o.id === order.id
          );

          if (index !== -1) {
            state.dispatcher.data[index] = order;
          } else {
            state.dispatcher.data.unshift(order);
          }
        });
      })
      .addCase(fetchDispatcherOrders.rejected, (state, action) => {
        state.dispatcher.loading = false;
        state.dispatcher.error =
          action.payload || "Error loading dispatcher orders";
      })

      .addCase(updateOrder.fulfilled, (state, action) => {
        const updatedOrder = action.payload;

        // Remove from unprocessed if it’s there
        state.unprocessed.data = state.unprocessed.data.filter(
          (o) => o.id !== updatedOrder.id
        );

        // If order now belongs to dispatcher, add/update in dispatcher list
        if (updatedOrder.dispatcher) {
          const index = state.dispatcher.data.findIndex(
            (o) => o.id === updatedOrder.id
          );
          if (index !== -1) {
            state.dispatcher.data[index] = updatedOrder;
          } else {
            state.dispatcher.data.push(updatedOrder);
          }
        }
      })

      .addCase(changeOrderStatus.fulfilled, (state, action) => {
        const updatedOrder = action.payload;

        // update in dispatcher list
        const index = state.dispatcher.data.findIndex(
          (o) => o.id === updatedOrder.id
        );
        if (index !== -1) {
          state.dispatcher.data[index] = updatedOrder;
        }

        // also update in unprocessed if exists
        const unIndex = state.unprocessed.data.findIndex(
          (o) => o.id === updatedOrder.id
        );
        if (unIndex !== -1) {
          state.unprocessed.data[unIndex] = updatedOrder;
        }
      });
  },
});

export const { cleanReducer, addUnprocessedOrder, mergeOrderUpdate } =
  ordersSlice.actions;
export default ordersSlice.reducer;
